import { parseISO } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

/** When there is no start date, treat the cycle as already started. */
export const checkCycleIsScheduled = (startDate: string | Date | null | undefined, status: string): boolean => {
	if (startDate == null || startDate === "") return true;
	const start = typeof startDate === "string" ? parseISO(startDate) : startDate;
	return start >= new Date() && status === "active";
};

export const formatCurrency = (value: number): string => {
	if (value >= 1_000_000_000) {
		return `$${(value / 1_000_000_000).toFixed(2)}B`;
	}
	if (value >= 1_000_000) {
		return `$${(value / 1_000_000).toFixed(2)}M`;
	}
	if (value >= 1_000) {
		return `$${(value / 1_000).toFixed(2)}K`;
	}
	return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatCurrencyFull = (value: number): string => {
	return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Format a number rounded to at most 2 decimal places, showing only significant decimals. */
export const formatConditionalDecimals = (value: number): string => {
	const rounded = Math.round(value * 100) / 100;
	return String(rounded);
};

/**
 * End of the selected calendar day in `timeZone`, as a UTC Date.
 * Uses the date picker's local calendar day (getFullYear/getMonth/getDate) so the chosen
 * day does not shift when company timezone differs from the browser (formatInTimeZone on
 * the instant would map midnight Eastern to the previous day in Central, etc.).
 */
export const dueDateToEndOfDayInTimeZone = (dueDate: Date, timeZone: string): Date => {
	const y = dueDate.getFullYear();
	const m = String(dueDate.getMonth() + 1).padStart(2, "0");
	const d = String(dueDate.getDate()).padStart(2, "0");
	const dateKey = `${y}-${m}-${d}`;
	return zonedTimeToUtc(`${dateKey} 23:59:59.999`, timeZone);
};

/**
 * Convert a stored UTC Date into a "fake-local" Date whose browser-local fields
 * (getFullYear, getMonth, getDate, getHours, …) match the wall-clock time in
 * `timeZone`. This is the inverse of `dateToTimeInTimeZone` and should be used
 * when loading stored UTC dates into the date picker for editing.
 */
export const utcToTimeInTimeZone = (date: Date, timeZone: string): Date => {
	return utcToZonedTime(date, timeZone);
};

/**
 * Reinterpret a date picker's local date+time as a wall-clock time in `timeZone`,
 * returning the corresponding UTC Date. The picker stores the user's chosen
 * year/month/day/hour/minute as browser-local values, but the UI tells the user
 * these represent company-timezone times. This function bridges that gap.
 */
export const dateToTimeInTimeZone = (date: Date, timeZone: string): Date => {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	const hh = String(date.getHours()).padStart(2, "0");
	const mm = String(date.getMinutes()).padStart(2, "0");
	const ss = String(date.getSeconds()).padStart(2, "0");
	return zonedTimeToUtc(`${y}-${m}-${d} ${hh}:${mm}:${ss}`, timeZone);
};
